<template>
    <div class="container">
      <h1 class="title">自动交易策略</h1>
      <div class="trade-info" v-if="isTradeActive">
        <p class="status">当前正在交易...</p>
        <p class="trade-detail">交易方向: <span class="highlight">{{ tradeSide }}</span></p>
        <p class="trade-detail">止盈/止损价格: 
          <span class="highlight">{{ takeProfitPrice }} / {{ stopLossPrice }}</span>
        </p>
      </div>
      <div v-else>
        <p class="status">没有正在进行的交易。</p>
      </div>
      
      <button class="trade-btn" @click="startTrading" :disabled="isTradeActive">启动自动交易</button>
  
      <div class="balance-info">
        <p class="balance">USDT余额: <span class="highlight">{{ usdtBalance }}</span></p>
        <p class="balance">BTC余额: <span class="highlight">{{ btcBalance }}</span></p>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  
  export default {
    data() {
      return {
        isTradeActive: false, // 是否有正在进行的交易
        usdtBalance: 0, // USDT余额
        btcBalance: 0, // BTC余额
        takeProfitPrice: 0, // 止盈价格
        stopLossPrice: 0, // 止损价格
        tradeSide: '', // 当前交易方向 (BUY/SELL)
      };
    },
    methods: {
      // 获取账户余额
      async getBalance() {
        const timestamp = Date.now();
        const queryString = `timestamp=${timestamp}`;
        const signature = this.signRequest(queryString);
  
        try {
          const response = await axios.get('https://api.binance.com/api/v3/account', {
            params: {
              timestamp: timestamp,
              signature: signature,
            },
            headers: {
              'X-MBX-APIKEY': 'your-api-key-here',
            },
          });
  
          const balances = response.data.balances;
          const usdtBalance = balances.find(item => item.asset === 'USDT');
          const btcBalance = balances.find(item => item.asset === 'BTC');
  
          this.usdtBalance = parseFloat(usdtBalance.free); // USDT余额
          this.btcBalance = parseFloat(btcBalance.free); // BTC余额
        } catch (error) {
          console.error('获取余额失败', error);
        }
      },
  
      // 获取市场数据和计算 EMA
      async getMarketData(symbol = 'BTCUSDT') {
        const response = await axios.get('https://api.binance.com/api/v1/klines', {
          params: {
            symbol: symbol,
            interval: '1m',
            limit: 60, // 获取过去60分钟的数据
          },
        });
  
        const closePrices = response.data.map(item => parseFloat(item[4])); // 收盘价
        const ema20 = this.calculateEMA(closePrices, 20); // 计算 EMA20
        const ema60 = this.calculateEMA(closePrices, 60); // 计算 EMA60
  
        return { ema20, ema60, currentPrice: closePrices[closePrices.length - 1] };
      },
  
      // 计算 EMA
      calculateEMA(prices, period) {
        const k = 2 / (period + 1);
        return prices.reduce((ema, price, index) => {
          if (index === 0) return price; // 使用第一天的价格作为 EMA 初始值
          return (price - ema) * k + ema;
        });
      },
  
      // 签名请求参数
      signRequest(queryString) {
        const apiSecret = 'your-api-secret-here';
        return require('crypto')
          .createHmac('sha256', apiSecret)
          .update(queryString)
          .digest('hex');
      },
  
      // 判断是否下单
      async checkAndPlaceOrder() {
        const { ema20, ema60, currentPrice } = await this.getMarketData();
  
        // 如果有正在进行的交易，则跳过
        if (this.isTradeActive) return;
  
        // 判断市场趋势
        if (ema20 > ema60) { // 涨
          if (currentPrice <= ema60) {
            // 执行买入操作
            await this.placeOrder('BUY');
          }
        } else if (ema20 < ema60) { // 跌
          if (currentPrice >= ema60) {
            // 执行卖出操作
            await this.placeOrder('SELL');
          }
        }
      },
  
      // 下单操作
      async placeOrder(side) {
        const quantity = 0.1; // 假设每次交易0.1BTC
        const timestamp = Date.now();
        const queryString = `symbol=BTCUSDT&side=${side}&type=MARKET&quantity=${quantity}&timestamp=${timestamp}`;
        const signature = this.signRequest(queryString);
  
        try {
          const response = await axios.post('https://api.binance.com/api/v3/order', null, {
            params: {
              symbol: 'BTCUSDT',
              side: side,
              type: 'MARKET',
              quantity: quantity,
              timestamp: timestamp,
              signature: signature,
            },
            headers: {
              'X-MBX-APIKEY': 'your-api-key-here',
            },
          });
  
          console.log('Order placed:', response.data);
          this.isTradeActive = true; // 标记交易已开始
          this.tradeSide = side; // 设置交易方向
  
          // 设置止盈止损
          this.setTakeProfitAndStopLoss(side, response.data);
        } catch (error) {
          console.error('Order placement failed:', error);
        }
      },
  
      // 设置止盈和止损
      setTakeProfitAndStopLoss(side, orderData) {
        const entryPrice = parseFloat(orderData.avgFillPrice); // 获取订单执行价格
        this.takeProfitPrice = side === 'BUY' ? entryPrice * 1.02 : entryPrice * 0.98;  // 止盈 2%
        this.stopLossPrice = side === 'BUY' ? entryPrice * 0.982 : entryPrice * 1.018;  // 止损 1.8%
  
        console.log('Setting Take Profit:', this.takeProfitPrice, 'Stop Loss:', this.stopLossPrice);
  
        // 设置止盈和止损订单（使用限价单或者条件单）
        this.placeTakeProfitStopLossOrder('SELL', this.takeProfitPrice, this.stopLossPrice);
      },
  
      // 设置止盈止损订单
      async placeTakeProfitStopLossOrder(side, takeProfitPrice, stopLossPrice) {
        const timestamp = Date.now();
        const queryString = `symbol=BTCUSDT&side=${side}&type=LIMIT&price=${takeProfitPrice}&quantity=0.1&timestamp=${timestamp}`;
        const signature = this.signRequest(queryString);
  
        try {
          const response = await axios.post('https://api.binance.com/api/v3/order', null, {
            params: {
              symbol: 'BTCUSDT',
              side: side,
              type: 'LIMIT',
              price: takeProfitPrice,
              quantity: 0.1,
              timestamp: timestamp,
              signature: signature,
            },
            headers: {
              'X-MBX-APIKEY': 'your-api-key-here',
            },
          });
  
          console.log('Take profit order placed:', response.data);
        } catch (error) {
          console.error('Error placing take profit order:', error);
        }
      },
  
      // 每分钟检查一次市场状态
      startTrading() {
        this.getBalance(); // 获取余额
        setInterval(async () => {
          await this.checkAndPlaceOrder();
        }, 60000); // 每分钟检查一次
      },
    },
  
    mounted() {
      this.startTrading(); // 启动自动交易
    },
  };
  </script>
  
  <style scoped>
  /* 页面布局 */
  .container {
    width: 80%;
    margin: 0 auto;
    padding: 20px;
    font-family: Arial, sans-serif;
  }
  
  .title {
    text-align: center;
    color: #333;
    font-size: 28px;
    margin-bottom: 20px;
  }
  
  /* 按钮样式 */
  .trade-btn {
    background-color: #28a745;
    color: #fff;
    padding: 10px 20px;
    font-size: 16px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    display: block;
    margin: 20px auto;
  }
  
  .trade-btn:disabled {
    background-color: #ddd;
    cursor: not-allowed;
  }
  
  .trade-btn:hover {
    background-color: #218838;
  }
  
  /* 状态文本 */
  .status {
    font-size: 18px;
    text-align: center;
  }
  
  .highlight {
    font-weight: bold;
    color: #007bff;
  }
  
  .trade-detail {
    font-size: 16px;
    margin: 10px 0;
  }
  
  /* 余额显示 */
  .balance-info {
    margin-top: 20px;
    text-align: center;
  }
  
  .balance {
    font-size: 18px;
  }
  
  .balance span.highlight {
    color: #17a2b8;
  }
  
  /* 信息容器 */
  .trade-info {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 5px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
  }
  </style>
  


// WEBPACK FOOTER //
// src/pages/ceshi/index.vue